import { useState } from 'react';
import { CreditCard, Calendar, ExternalLink, QrCode, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Button } from '@/components/ui/button';
import { Pill } from '@/components/ui/pill';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { SubscribeDrawer } from './SubscribeDrawer';
import { PixPaymentModal } from './PixPaymentModal';
import {
  useSubscription,
  useCancelSubscription,
  formatBrl,
  statusLabel,
  statusTone,
  paymentMethodLabel,
  type SubscriptionStatus,
} from '@/lib/hooks/billing';

export function AssinaturaCard() {
  const { data, isLoading } = useSubscription();
  const cancelMut = useCancelSubscription();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [pixPaymentId, setPixPaymentId] = useState<string | null>(null);

  const sub = data?.subscription;
  const lastPay = data?.lastPayment;

  async function handleCancel() {
    try {
      await cancelMut.mutateAsync();
      toast.success('Assinatura cancelada.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cancelar.');
    } finally {
      setConfirmCancel(false);
    }
  }

  return (
    <>
      <Card variant="default" className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2 bg-mint-soft text-mint-deep shrink-0">
              <CreditCard className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <Eyebrow>Assinatura</Eyebrow>
              <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
                Plano e cobrança
              </h3>
              <p className="text-sm text-fg-muted mt-0.5">
                Sua assinatura mensal do GestAKI.
              </p>
            </div>
          </div>
          {sub && <Pill tone={statusTone(sub.status as SubscriptionStatus)} dot>{statusLabel(sub.status as SubscriptionStatus)}</Pill>}
        </div>

        {isLoading ? (
          <div className="h-20 rounded-2 bg-card-soft animate-pulse" />
        ) : !sub ? (
          <div className="rounded-2 border border-dashed border-card-stroke bg-card-soft p-6 text-center">
            <p className="text-sm text-fg-muted">Você ainda não tem uma assinatura ativa.</p>
            <Button variant="primary" size="sm" className="mt-3" onClick={() => setDrawerOpen(true)}>
              Assinar agora
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <Field label="Plano" value={planName(sub.plan)} />
              <Field label="Valor" value={`${formatBrl(sub.value)} / ${sub.cycle === 'YEARLY' ? 'ano' : 'mês'}`} />
              <Field label="Forma de pagamento" value={paymentMethodLabel(sub.paymentMethod)} />
              <Field
                label="Próxima cobrança"
                value={sub.nextDueDate ? formatDate(sub.nextDueDate) : '—'}
                icon={Calendar}
              />
            </div>

            {lastPay && (
              <div className="rounded-2 border border-card-stroke bg-card-soft p-3 mb-4">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <span className="text-[11px] uppercase tracking-wider font-mono text-fg-muted">
                    Última cobrança
                  </span>
                  <Pill tone={paymentTone(lastPay.status)} dot>
                    {paymentStatusLabel(lastPay.status)}
                  </Pill>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-fg">
                    {formatBrl(lastPay.value)} · vence {formatDate(lastPay.dueDate)}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {lastPay.paymentMethod === 'PIX' && lastPay.status === 'PENDING' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPixPaymentId(lastPay.id)}
                      >
                        <QrCode className="h-3.5 w-3.5" strokeWidth={1.8} />
                        Ver PIX
                      </Button>
                    )}
                    {lastPay.bankSlipUrl && (
                      <a
                        href={lastPay.bankSlipUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-mint-deep hover:underline px-2 py-1"
                      >
                        <FileText className="h-3.5 w-3.5" strokeWidth={1.8} />
                        Boleto
                      </a>
                    )}
                    {lastPay.invoiceUrl && (
                      <a
                        href={lastPay.invoiceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-fg-muted hover:text-fg px-2 py-1"
                      >
                        <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.8} />
                        Fatura
                      </a>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-end gap-2">
              {sub.status !== 'CANCELLED' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmCancel(true)}
                  className="text-warn hover:bg-warn-soft"
                >
                  Cancelar assinatura
                </Button>
              )}
              <Button variant="primary" size="sm" onClick={() => setDrawerOpen(true)}>
                {sub.status === 'CANCELLED' ? 'Reativar' : 'Trocar plano'}
              </Button>
            </div>
          </>
        )}
      </Card>

      <SubscribeDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        currentPlan={sub?.plan ?? null}
      />

      <PixPaymentModal
        paymentId={pixPaymentId}
        onClose={() => setPixPaymentId(null)}
      />

      <ConfirmDialog
        open={confirmCancel}
        onOpenChange={setConfirmCancel}
        title="Cancelar assinatura?"
        description="Você perde o acesso ao final do ciclo atual. Pode reativar depois."
        confirmLabel="Cancelar assinatura"
        tone="danger"
        loading={cancelMut.isPending}
        onConfirm={handleCancel}
      />
    </>
  );
}

function Field({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon?: typeof Calendar;
}) {
  return (
    <div>
      <span className="block font-mono text-[10px] uppercase tracking-[0.10em] text-fg-muted mb-1">
        {label}
      </span>
      <div className="flex items-center gap-1.5 text-sm font-medium text-fg">
        {Icon && <Icon className="h-3.5 w-3.5 text-fg-muted" strokeWidth={1.8} />}
        <span>{value}</span>
      </div>
    </div>
  );
}

function planName(plan: string): string {
  return { BASIC: 'Básico', PRO: 'Profissional', ENTERPRISE: 'Empresarial' }[plan] ?? plan;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

function paymentStatusLabel(s: string): string {
  return (
    {
      PENDING: 'Aguardando',
      CONFIRMED: 'Pago',
      OVERDUE: 'Em atraso',
      REFUNDED: 'Estornado',
      CHARGEBACK: 'Contestado',
      DELETED: 'Cancelado',
    } as Record<string, string>
  )[s] ?? s;
}

function paymentTone(s: string): 'mint' | 'warn' | 'lilac' | 'block' {
  if (s === 'CONFIRMED') return 'mint';
  if (s === 'PENDING') return 'warn';
  if (s === 'OVERDUE' || s === 'CHARGEBACK') return 'block';
  return 'lilac';
}
